import axios from 'axios';
import { logger, clearSessionAndCookie } from '@marriott/shared/mi-helper-utils';
import { SESSION_TIMED_OUT_API, TAB_DIRTY_SESSION } from './constants';

const handleSessionTimedOut = async (apiUrl: string, returnUrl?: string) => {
  return await axios
    .post(
      apiUrl,
      {
        returnTo: returnUrl,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
    .then(data => {
      clearSessionAndCookie();
      if (data?.data?.redirect) {
        window.location.href = data?.data?.nextStateURI;
      }
    })
    .catch(error => {
      const { log } = logger({})('SessionTimedOut');
      log.error('Session Timed Out failed', error);
    });
};

let timerId = -1;
let sessionTimeoutHandler: (() => void) | null = null;
let storageEventHandler: ((event: StorageEvent) => void) | null = null;

export const setupSessionTimeout = ({
  expiryTime,
  apiUrl = SESSION_TIMED_OUT_API,
}: {
  expiryTime?: string;
  apiUrl?: string;
}) => {
  const sessionExpiryTime = Number(expiryTime);
  if (sessionExpiryTime && sessionExpiryTime > 0) {
    clearTimeout(timerId);
    timerId = window.setTimeout(async () => {
      const location = window?.location;
      const returnUrl = location?.pathname + location?.search + location?.hash;
      await handleSessionTimedOut(apiUrl, returnUrl);
    }, sessionExpiryTime);
    sessionStorage.setItem(TAB_DIRTY_SESSION, 'false');
  }
};

export const initializeSessionTimeout = (expiryTime?: string, apiURL?: string) => {
  // reset session on user actions
  sessionStorage.setItem(TAB_DIRTY_SESSION, 'true');

  sessionTimeoutHandler = () => setupSessionTimeout({ expiryTime, apiUrl: apiURL });
  ['click', 'touchstart', 'keydown', 'contextmenu'].forEach(evt =>
    document.addEventListener(evt, sessionTimeoutHandler!)
  );

  // to handle change in localstorage value from another window/tab
  storageEventHandler = event => handleStorageEvent(event, expiryTime, apiURL);
  window.addEventListener('storage', storageEventHandler);

  setupSessionTimeout({ expiryTime, apiUrl: apiURL });
};

export const clearSessionTimeout = () => {
  clearTimeout(timerId);
  if (sessionTimeoutHandler) {
    ['click', 'touchstart', 'keydown', 'contextmenu'].forEach(evt =>
      document.removeEventListener(evt, sessionTimeoutHandler!)
    );
    sessionTimeoutHandler = null;
  }
  sessionStorage.setItem(TAB_DIRTY_SESSION, 'false');
  if (storageEventHandler) {
    window.removeEventListener('storage', storageEventHandler);
    storageEventHandler = null;
  }
};

const handleStorageEvent = (storageEvent: StorageEvent, expiryTime?: string, apiURL?: string): void => {
  if (storageEvent.key === TAB_DIRTY_SESSION && storageEvent.newValue === 'true') {
    setupSessionTimeout({ expiryTime, apiUrl: apiURL });
  }
};
